import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, TrendingUp, Grid3x3, Globe, Trash2, Plus, RefreshCw, Download, ArrowRightCircle, X as XIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { API, api, endpoints, getTenantScope } from "@/lib/api";
import { toast } from "sonner";

const RATING_TONE = { High: "bg-rose-100 text-rose-800 border-rose-200", Medium: "bg-amber-100 text-amber-800 border-amber-200", Low: "bg-emerald-100 text-emerald-800 border-emerald-200" };
const STATUS_TONE = { Open: "bg-sky-50 text-sky-700 border-sky-200", Monitoring: "bg-indigo-50 text-indigo-700 border-indigo-200", Mitigated: "bg-emerald-50 text-emerald-700 border-emerald-200", Closed: "bg-slate-100 text-slate-600 border-slate-200" };
const SWOT_TONE = {
  Strengths: { bg: "from-emerald-500 to-teal-600", light: "bg-emerald-50" },
  Weaknesses: { bg: "from-rose-500 to-pink-600", light: "bg-rose-50" },
  Opportunities: { bg: "from-indigo-500 to-purple-600", light: "bg-indigo-50" },
  Threats: { bg: "from-amber-500 to-orange-600", light: "bg-amber-50" },
};
const PESTLE_TONE = {
  Political: "text-rose-700 bg-rose-50", Economic: "text-emerald-700 bg-emerald-50",
  Social: "text-indigo-700 bg-indigo-50", Technological: "text-cyan-700 bg-cyan-50",
  Legal: "text-slate-700 bg-slate-100", Environmental: "text-lime-700 bg-lime-50",
};
const fmt = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";
const rating = (l, i) => { const v = (l || 0) * (i || 0); return v >= 15 ? "High" : v >= 8 ? "Medium" : "Low"; };

async function apiGet(p) { const r = await api.get(p); return r.data; }
async function apiPost(p, b) { const r = await api.post(p, b); return r.data; }
async function apiPatch(p, b) { const r = await api.patch(p, b); return r.data; }
async function apiDel(p) { const r = await api.delete(p); return r.data; }

export default function RisksOpportunitiesPage() {
  const scope = getTenantScope();
  const [params] = useSearchParams();
  const qp = params.get("client");
  const [clientSlug, setClientSlug] = useState(qp || scope?.slug || "northwind-corp");
  const [clients, setClients] = useState([]);
  const [risks, setRisks] = useState([]);
  const [swot, setSwot] = useState([]);
  const [pestle, setPestle] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [tab, setTab] = useState("register");
  const [openAdd, setOpenAdd] = useState(false);
  const [editRisk, setEditRisk] = useState(null);
  const [xferOpen, setXferOpen] = useState(null); // { source, item }
  const isClient = ["client", "client_admin", "client_employee"].includes(scope?.role);

  const load = () => {
    endpoints.listClients().then(setClients).catch(() => {});
    apiGet(`/risks?client_slug=${clientSlug}`).then(setRisks).catch(() => setRisks([]));
    apiGet(`/swot?client_slug=${clientSlug}`).then(setSwot).catch(() => setSwot([]));
    apiGet(`/pestle?client_slug=${clientSlug}`).then(setPestle).catch(() => setPestle([]));
    apiGet(`/risks-dashboard?client_slug=${clientSlug}`).then(setDashboard).catch(() => setDashboard(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientSlug]);

  const deleteRisk = async (id) => { try { await apiDel(`/risks/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const patchRisk = async (id, p) => { try { await apiPatch(`/risks/${id}`, p); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-rose-500 to-indigo-600 text-white flex items-center justify-center shadow-md"><AlertTriangle className="h-5 w-5" /></span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Risks & Opportunities</h1>
            <p className="text-xs text-slate-500">Register · SWOT · PESTLE — connected for audits & Management Review.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isClient && (
            <Select value={clientSlug} onValueChange={setClientSlug}>
              <SelectTrigger className="w-48" data-testid="ro-client-select"><SelectValue /></SelectTrigger>
              <SelectContent>{clients.map(c => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          <a href={`${API}/risks-export.csv?client_slug=${clientSlug}`} className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50" data-testid="ro-export">
            <Download className="h-4 w-4" /> Export
          </a>
          <Button data-testid="ro-add-risk" className="brand-gradient text-white" onClick={() => { setEditRisk(null); setOpenAdd(true); }}>
            <Plus className="h-4 w-4 mr-1" /> Add risk / opportunity
          </Button>
        </div>
      </div>

      {/* Dashboard */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        <Stat label="Total items" value={dashboard?.counts?.total ?? 0} tone="indigo" testid="ro-stat-total" />
        <Stat label="High risks" value={dashboard?.counts?.high_risks ?? 0} tone="rose" testid="ro-stat-high" />
        <Stat label="Open opportunities" value={dashboard?.counts?.open_opportunities ?? 0} tone="emerald" testid="ro-stat-opps" />
        <Stat label="Overdue actions" value={dashboard?.counts?.overdue_actions ?? 0} tone="amber" testid="ro-stat-overdue" />
        <Stat label="Review due" value={dashboard?.counts?.review_due ?? 0} tone="sky" testid="ro-stat-review" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="register" data-testid="ro-tab-register">Register</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="ro-tab-matrix">Risk Matrix</TabsTrigger>
          <TabsTrigger value="swot" data-testid="ro-tab-swot">SWOT</TabsTrigger>
          <TabsTrigger value="pestle" data-testid="ro-tab-pestle">PESTLE</TabsTrigger>
        </TabsList>

        <TabsContent value="register" className="pt-4">
          <Card className="glass-card"><CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/70 border-b"><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="py-2 px-3">Item</th><th className="py-2 px-3">Category</th><th className="py-2 px-3">Source</th>
                  <th className="py-2 px-3">L</th><th className="py-2 px-3">I</th><th className="py-2 px-3">Rating</th>
                  <th className="py-2 px-3">Responsible</th><th className="py-2 px-3">Due</th><th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Review</th><th className="py-2 px-3"></th>
                </tr></thead>
                <tbody>
                  {risks.map(r => (
                    <tr key={r.id} data-testid={`ro-row-${r.id}`} className={`border-b hover:bg-slate-50/60 cursor-pointer ${r.overdue ? "bg-rose-50/30" : ""}`} onClick={() => { setEditRisk(r); setOpenAdd(true); }}>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1 font-medium">{r.is_opportunity && <TrendingUp className="h-3 w-3 text-emerald-600" />} {r.title}</div>
                        {r.actions_required && <div className="text-[10px] text-slate-500 truncate max-w-[280px]">→ {r.actions_required}</div>}
                      </td>
                      <td className="py-2 px-3 text-xs">{r.category}</td>
                      <td className="py-2 px-3"><Badge variant="secondary" className="text-[10px]">{r.source}</Badge></td>
                      <td className="py-2 px-3 font-mono">{r.likelihood}</td>
                      <td className="py-2 px-3 font-mono">{r.impact}</td>
                      <td className="py-2 px-3"><Badge className={RATING_TONE[r.rating]}>{r.rating}</Badge></td>
                      <td className="py-2 px-3">{r.responsible_person || "—"}</td>
                      <td className={`py-2 px-3 text-xs ${r.overdue ? "text-rose-700 font-semibold" : ""}`}>{fmt(r.due_date)}</td>
                      <td className="py-2 px-3"><Badge className={STATUS_TONE[r.status]}>{r.status}</Badge></td>
                      <td className={`py-2 px-3 text-xs ${r.review_due ? "text-amber-700 font-semibold" : ""}`}>{fmt(r.review_date)}</td>
                      <td className="py-2 px-3" onClick={e => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" onClick={() => deleteRisk(r.id)} data-testid={`ro-del-${r.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                      </td>
                    </tr>
                  ))}
                  {risks.length === 0 && <tr><td colSpan={11} className="text-center py-10 text-slate-500">No risks or opportunities yet — start with the SWOT or PESTLE tab.</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="matrix" className="pt-4">
          <Card className="glass-card"><CardContent className="p-6">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2"><Grid3x3 className="h-4 w-4 text-indigo-600" /> Likelihood × Impact matrix</div>
            <div className="relative">
              <div className="grid grid-cols-6 gap-1">
                <div className="col-span-1"></div>
                {[1, 2, 3, 4, 5].map(l => <div key={l} className="text-center text-[10px] uppercase tracking-widest text-slate-500 font-semibold">L{l}</div>)}
                {[5, 4, 3, 2, 1].map(i => (
                  <React.Fragment key={i}>
                    <div className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-semibold pt-3">I{i}</div>
                    {[1, 2, 3, 4, 5].map(l => {
                      const rate = rating(l, i);
                      const bg = rate === "High" ? "bg-rose-100 border-rose-300 text-rose-800" : rate === "Medium" ? "bg-amber-100 border-amber-300 text-amber-800" : "bg-emerald-100 border-emerald-300 text-emerald-800";
                      const count = dashboard?.matrix?.[5 - i]?.[l - 1] ?? 0;
                      return (
                        <div key={l} className={`h-16 border rounded ${bg} flex items-center justify-center text-xl font-extrabold`} data-testid={`ro-matrix-${l}-${i}`}>
                          {count || <span className="text-[10px] font-normal opacity-40">·</span>}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
              <div className="flex gap-4 mt-4 text-xs">
                <span><span className="inline-block h-3 w-3 rounded bg-emerald-300 mr-1" /> Low (score 1–7)</span>
                <span><span className="inline-block h-3 w-3 rounded bg-amber-300 mr-1" /> Medium (8–14)</span>
                <span><span className="inline-block h-3 w-3 rounded bg-rose-300 mr-1" /> High (15+)</span>
              </div>
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="swot" className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["Strengths", "Weaknesses", "Opportunities", "Threats"].map(q => (
              <SWOTQuadrant key={q} quadrant={q} clientSlug={clientSlug} items={swot.filter(i => i.quadrant === q)}
                onAdded={load} onDeleted={load}
                onTransfer={(item) => setXferOpen({ source: "swot", item })} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="pestle" className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {["Political", "Economic", "Social", "Technological", "Legal", "Environmental"].map(f => (
              <PESTLEFactor key={f} factor={f} clientSlug={clientSlug} items={pestle.filter(i => i.factor === f)}
                onAdded={load} onDeleted={load}
                onTransfer={(item) => setXferOpen({ source: "pestle", item })} />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <RiskModal open={openAdd} onOpenChange={setOpenAdd} clientSlug={clientSlug} risk={editRisk} onSaved={load} />
      <TransferModal state={xferOpen} onOpenChange={(o) => !o && setXferOpen(null)} onDone={load} />
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const c = { indigo: "text-indigo-600", rose: "text-rose-600", emerald: "text-emerald-600", amber: "text-amber-600", sky: "text-sky-600" }[tone] || "text-slate-700";
  return <div data-testid={testid} className="rounded-xl border bg-white p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div><div className={`text-2xl font-extrabold mt-0.5 ${c}`}>{value}</div></div>;
}

function SWOTQuadrant({ quadrant, clientSlug, items, onAdded, onDeleted, onTransfer }) {
  const [text, setText] = useState("");
  const [action, setAction] = useState("");
  const tone = SWOT_TONE[quadrant];
  const add = async () => {
    if (!text.trim()) return;
    try { await apiPost(`/swot/${quadrant}`, { client_slug: clientSlug, text, action }); setText(""); setAction(""); onAdded?.(); toast.success(`Added to ${quadrant}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { try { await apiDel(`/swot/item/${id}`); onDeleted?.(); } catch (e) { toast.error("Failed"); } };
  return (
    <Card className={`glass-card`}><CardContent className="p-0">
      <div className={`bg-gradient-to-br ${tone.bg} text-white px-4 py-2 rounded-t-xl font-semibold flex items-center justify-between`}>
        <span>{quadrant}</span><span className="text-xs opacity-80">{items.length}</span>
      </div>
      <div className={`${tone.light} p-3 space-y-2`}>
        {items.map(it => (
          <div key={it.id} className="rounded border bg-white p-2" data-testid={`swot-item-${it.id}`}>
            <div className="flex justify-between gap-2">
              <div className="flex-1 text-sm">{it.text}
                {it.action && <div className="text-[10px] text-slate-500 mt-0.5">→ {it.action}</div>}
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onTransfer(it)} title="Transfer to risk register" data-testid={`swot-transfer-${it.id}`}><ArrowRightCircle className="h-4 w-4 text-indigo-500" /></Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => del(it.id)}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
              </div>
            </div>
            {it.transferred_risk_id && <Badge variant="secondary" className="text-[9px] mt-1">In register</Badge>}
          </div>
        ))}
        <div className="rounded border bg-white/70 p-2">
          <Textarea rows={2} placeholder={`Add a ${quadrant.slice(0, -1).toLowerCase()}…`} value={text} onChange={e => setText(e.target.value)} data-testid={`swot-input-${quadrant}`} />
          <Input className="mt-1 text-xs" placeholder="Action (optional)" value={action} onChange={e => setAction(e.target.value)} />
          <Button size="sm" onClick={add} className="mt-1 brand-gradient text-white w-full" data-testid={`swot-add-${quadrant}`}><Plus className="h-3 w-3 mr-1" /> Add</Button>
        </div>
      </div>
    </CardContent></Card>
  );
}

function PESTLEFactor({ factor, clientSlug, items, onAdded, onDeleted, onTransfer }) {
  const [text, setText] = useState("");
  const [action, setAction] = useState("");
  const tone = PESTLE_TONE[factor];
  const add = async () => {
    if (!text.trim()) return;
    try { await apiPost(`/pestle/${factor}`, { client_slug: clientSlug, text, action }); setText(""); setAction(""); onAdded?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { try { await apiDel(`/pestle/item/${id}`); onDeleted?.(); } catch (e) { toast.error("Failed"); } };
  return (
    <Card className="glass-card"><CardContent className="p-0">
      <div className={`px-3 py-2 rounded-t-xl font-semibold ${tone} flex items-center justify-between`}>
        <span><Globe className="h-3 w-3 inline mr-1" />{factor}</span><span className="text-xs opacity-70">{items.length}</span>
      </div>
      <div className="p-3 space-y-2">
        {items.map(it => (
          <div key={it.id} className="rounded border bg-white p-2" data-testid={`pestle-item-${it.id}`}>
            <div className="flex justify-between gap-2">
              <div className="flex-1 text-sm">{it.text}
                {it.action && <div className="text-[10px] text-slate-500 mt-0.5">→ {it.action}</div>}
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onTransfer(it)} data-testid={`pestle-transfer-${it.id}`}><ArrowRightCircle className="h-4 w-4 text-indigo-500" /></Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => del(it.id)}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
              </div>
            </div>
            {it.transferred_risk_id && <Badge variant="secondary" className="text-[9px] mt-1">In register</Badge>}
          </div>
        ))}
        <Textarea rows={2} placeholder={`Add ${factor}…`} value={text} onChange={e => setText(e.target.value)} data-testid={`pestle-input-${factor}`} />
        <Input className="text-xs" placeholder="Action (optional)" value={action} onChange={e => setAction(e.target.value)} />
        <Button size="sm" onClick={add} className="brand-gradient text-white w-full" data-testid={`pestle-add-${factor}`}><Plus className="h-3 w-3 mr-1" /> Add</Button>
      </div>
    </CardContent></Card>
  );
}

function RiskModal({ open, onOpenChange, clientSlug, risk, onSaved }) {
  const empty = { title: "", description: "", category: "Operational", is_opportunity: false, likelihood: 3, impact: 3, existing_controls: "", actions_required: "", responsible_person: "", due_date: "", review_date: "", status: "Open" };
  const [f, setF] = useState(empty);
  useEffect(() => { setF(risk ? { ...empty, ...risk } : empty); /* eslint-disable-next-line */ }, [risk, open]);
  const r = rating(f.likelihood, f.impact);
  const submit = async () => {
    if (!f.title) { toast.error("Title required"); return; }
    try {
      if (risk?.id) await apiPatch(`/risks/${risk.id}`, f);
      else await apiPost(`/risks`, { client_slug: clientSlug, ...f });
      toast.success(risk?.id ? "Updated" : "Added"); onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ro-risk-modal">
        <DialogHeader><DialogTitle>{risk?.id ? "Edit" : "Add"} risk / opportunity</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Title *" span2><Input data-testid="ro-risk-title" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="Type">
            <Select value={f.is_opportunity ? "opp" : "risk"} onValueChange={v => setF({ ...f, is_opportunity: v === "opp" })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="risk">Risk</SelectItem><SelectItem value="opp">Opportunity</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Category">
            <Select value={f.category} onValueChange={v => setF({ ...f, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["Operational", "Strategic", "Financial", "Compliance", "Health & Safety", "Environmental", "Reputation", "Supply Chain", "IT / Cyber", "Other"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={`Likelihood (${f.likelihood})`}><input type="range" min="1" max="5" value={f.likelihood} onChange={e => setF({ ...f, likelihood: Number(e.target.value) })} data-testid="ro-risk-likelihood" className="w-full" /></Field>
          <Field label={`Impact (${f.impact})`}><input type="range" min="1" max="5" value={f.impact} onChange={e => setF({ ...f, impact: Number(e.target.value) })} data-testid="ro-risk-impact" className="w-full" /></Field>
          <Field label="Existing controls" span2><Textarea rows={2} value={f.existing_controls} onChange={e => setF({ ...f, existing_controls: e.target.value })} /></Field>
          <Field label="Actions required" span2><Textarea rows={2} value={f.actions_required} onChange={e => setF({ ...f, actions_required: e.target.value })} /></Field>
          <Field label="Responsible person"><Input value={f.responsible_person} onChange={e => setF({ ...f, responsible_person: e.target.value })} /></Field>
          <Field label="Status">
            <Select value={f.status} onValueChange={v => setF({ ...f, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["Open", "Monitoring", "Mitigated", "Closed"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Due date"><Input type="date" value={f.due_date || ""} onChange={e => setF({ ...f, due_date: e.target.value })} /></Field>
          <Field label="Review date"><Input type="date" value={f.review_date || ""} onChange={e => setF({ ...f, review_date: e.target.value })} /></Field>
        </div>
        <div className="flex items-center justify-between px-1 py-2 border-t mt-2 text-sm">
          <span>Preview rating:</span> <Badge className={RATING_TONE[r]}>{r} ({f.likelihood * f.impact})</Badge>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button data-testid="ro-risk-save" className="brand-gradient text-white" onClick={submit}>{risk?.id ? "Save" : "Add"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferModal({ state, onOpenChange, onDone }) {
  const [l, setL] = useState(3);
  const [i, setI] = useState(3);
  const [cat, setCat] = useState("Strategic");
  useEffect(() => { setL(3); setI(3); setCat("Strategic"); }, [state]);
  if (!state) return null;
  const { source, item } = state;
  const submit = async () => {
    try {
      const p = { likelihood: l, impact: i, category: cat };
      const path = source === "swot" ? `/swot/item/${item.id}/transfer-to-risk` : `/pestle/item/${item.id}/transfer-to-risk`;
      await apiPost(path, p);
      toast.success("Transferred to register"); onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={!!state} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ro-transfer-modal">
        <DialogHeader><DialogTitle>Transfer to Risk Register</DialogTitle>
          <DialogDescription>Send this {source.toUpperCase()} item into the connected register with an initial score.</DialogDescription></DialogHeader>
        <div className="rounded border bg-slate-50 p-3 text-sm">{item.text}</div>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Category">
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["Operational", "Strategic", "Financial", "Compliance", "Health & Safety", "Environmental", "Reputation", "Supply Chain", "IT / Cyber", "Other"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div></div>
          <Field label={`Likelihood (${l})`}><input type="range" min="1" max="5" value={l} onChange={e => setL(Number(e.target.value))} className="w-full" /></Field>
          <Field label={`Impact (${i})`}><input type="range" min="1" max="5" value={i} onChange={e => setI(Number(e.target.value))} className="w-full" /></Field>
        </div>
        <div className="text-xs text-center pt-2">Rating: <Badge className={RATING_TONE[rating(l, i)]}>{rating(l, i)}</Badge></div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button data-testid="ro-transfer-save" className="brand-gradient text-white" onClick={submit}>Transfer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, span2 = false }) { return <div className={span2 ? "sm:col-span-2" : ""}><Label className="text-xs">{label}</Label>{children}</div>; }
